'use client';

import { motion } from 'framer-motion';
import { PortableText } from '@portabletext/react';
import { richTextComponent } from '@/sanity/sanityPortableText';
import { ReviewCard } from '@/components/global/sections/Reviews';
import { PortableTextBlock } from 'sanity';
import { cn } from '@/lib/utils';
import ChipSerif from '@/components/global/ui/ChipSerif';
import { CTAButton } from '@/shadcn/ui/button';
import { GROQButton } from '@/sanity/lib/definitions';
import { resolveI18nRichtext, DEFAULT_LANGUAGE, II18nRichtext } from '@/sanity/globals/I18n';

interface ReviewsProp {
  className?: string;
  classNameReviewsSection?: string;
  reviewsChip?: string;
  reviewsTitle: PortableTextBlock[];
  reviewsCTA?: GROQButton;
  reviewsDocument: {
    _id: string;
    name: string;
    reviews: {
      content: PortableTextBlock[];
      author: string;
      i18n?: II18nRichtext;
    }[];
  };
  fadeColor?: 'white' | 'green' | 'grey' | 'blue';
  slug?: string;
  language?: string;
  cardTheme?: 'white' | 'green';
}

export default function ReviewsSection({
  reviewsChip,
  reviewsTitle,
  reviewsDocument,
  reviewsCTA,
  className,
  classNameReviewsSection,
  fadeColor = 'white',
  slug,
  language = DEFAULT_LANGUAGE,
  cardTheme = 'green',
}: ReviewsProp) {

  const distributeReviews = () => {
    const reviews = reviewsDocument?.reviews;
    if (!reviews || reviews.length === 0) return [[], [], []];
    const col1: typeof reviews = [];
    const col2: typeof reviews = [];
    const col3: typeof reviews = [];
    reviews.forEach((review, index) => {
      if (index % 3 === 0) col1.push(review);
      else if (index % 3 === 1) col2.push(review);
      else col3.push(review);
    });
    return [col1, col2, col3];
  };

  const [reviewCol1, reviewCol2, reviewCol3] = distributeReviews();

  const fadeColorTailwind =
    fadeColor === 'white'
      ? 'from-white'
      : fadeColor === 'green'
        ? 'from-[#E6EDE9]'
        : fadeColor === 'blue'
          ? 'from-[#07171B]'
          : 'from-[#F6F9F7]';

  const resolveReview = (review: { content: PortableTextBlock[]; author: string; i18n?: II18nRichtext }) => ({
    content: resolveI18nRichtext(review.content, review.i18n, language) ?? review.content,
    author: review.author,
  });

  return (
    <section
      id="reviews"
      className={cn(
        'w-full h-full py-[7rem] lg:py-[16rem] px-[2rem] lg:px-0',
        className,
      )}
    >
      <div className="flex relative flex-col items-center mx-auto w-full max-w-[177.25rem] overflow-clip">
        {reviewsChip && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-100px' }}
            transition={{ duration: 0.3, ease: [0.25, 0.46, 0.45, 0.94] }}
          >
            <ChipSerif text={reviewsChip} />
          </motion.div>
        )}

        {reviewsTitle && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-100px' }}
            transition={{
              duration: 0.3,
              delay: 0.3,
              ease: [0.25, 0.46, 0.45, 0.94],
            }}
            className={cn(
              'richtext',
              language === 'br'
                ? 'w-[min(115rem,100%)] prose-p:w-[min(75.6rem,100%)]'
                : 'w-[min(92rem,95%)] prose-p:w-[min(66.625rem,100%)]',
            )}
          >
            <PortableText value={reviewsTitle} components={richTextComponent} />
          </motion.div>
        )}

        {reviewsDocument?.reviews && reviewsDocument.reviews.length > 0 && (
          <motion.section
            className={cn('pt-[5rem] lg:pt-[10rem]', classNameReviewsSection)}
            initial={{ filter: 'blur(10px)', opacity: 0 }}
            whileInView={{ filter: 'blur(0px)', opacity: 1 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.45, ease: 'easeOut' }}
          >
            {/* Mobile - Single Column */}
            <div className="block relative mx-auto w-full lg:hidden h-[110.25rem]">
              <div className="overflow-hidden relative h-full">
                <div
                  className="flex flex-col gap-[2.5rem]"
                  style={{
                    height: `calc(${reviewsDocument.reviews.length * 2} * 40rem + ${reviewsDocument.reviews.length * 2 * 2.5}rem)`,
                    animation: `scrollUp ${reviewsDocument.reviews.length * 10}s linear infinite`,
                  }}
                >
                  {[...reviewsDocument.reviews, ...reviewsDocument.reviews].map(
                    (review, index) => (
                      <ReviewCard
                        key={'colMreview' + index}
                        review={resolveReview(review)}
                        className="shrink-0"
                        theme={cardTheme}
                      />
                    ),
                  )}
                </div>
              </div>
              <div
                className={cn(
                  'absolute -top-px left-0 z-10 w-full bg-gradient-to-b to-transparent pointer-events-none h-[15rem]',
                  fadeColorTailwind,
                )}
              />
              <div
                className={cn(
                  'absolute -bottom-px left-0 z-10 w-full bg-gradient-to-t to-transparent pointer-events-none h-[15rem]',
                  fadeColorTailwind,
                )}
              />
            </div>

            {/* Desktop - Three Columns */}
            <div className="hidden lg:block w-[min(173.25rem,100%)] mx-auto relative overflow-y-clip h-[110.35rem]">
              <div className="grid grid-cols-3 h-full gap-[2.5rem] pt-[.125rem]">
                {reviewCol1.length > 0 && (
                  <div className="overflow-x-visible relative h-full">
                    <div
                      className="flex flex-col gap-[2.5rem]"
                      style={{
                        height: `calc(${reviewCol1.length * 2} * 40rem + ${reviewCol1.length * 2 * 2.5}rem)`,
                        animation: `scrollUp ${reviewCol1.length * 10}s linear infinite`,
                      }}
                    >
                      {[...reviewCol1, ...reviewCol1].map((review, index) => (
                        <ReviewCard
                          key={'col1review' + index}
                          review={resolveReview(review)}
                          className="shrink-0"
                          theme={cardTheme}
                        />
                      ))}
                    </div>
                  </div>
                )}
                {reviewCol2.length > 0 && (
                  <div className="overflow-x-visible relative h-full">
                    <div
                      className="flex flex-col gap-[2.5rem]"
                      style={{
                        height: `calc(${reviewCol2.length * 2} * 40rem + ${reviewCol2.length * 2 * 2.5}rem)`,
                        animation: `scrollDown ${reviewCol2.length * 10}s linear infinite`,
                      }}
                    >
                      {[...reviewCol2, ...reviewCol2].map((review, index) => (
                        <ReviewCard
                          key={'col2review' + index}
                          review={resolveReview(review)}
                          className="shrink-0"
                          theme={cardTheme}
                        />
                      ))}
                    </div>
                  </div>
                )}
                {reviewCol3.length > 0 && (
                  <div className="overflow-x-visible relative h-full">
                    <div
                      className="flex flex-col gap-[2.5rem]"
                      style={{
                        height: `calc(${reviewCol3.length * 2} * 40rem + ${reviewCol3.length * 2 * 2.5}rem)`,
                        animation: `scrollUp ${reviewCol3.length * 10}s linear infinite`,
                      }}
                    >
                      {[...reviewCol3, ...reviewCol3].map((review, index) => (
                        <ReviewCard
                          key={'col3review' + index}
                          review={resolveReview(review)}
                          className="shrink-0"
                          theme={cardTheme}
                        />
                      ))}
                    </div>
                  </div>
                )}
              </div>
              <div
                className={cn(
                  'absolute -top-0 left-0 z-10 w-full bg-gradient-to-b to-transparent pointer-events-none h-[15rem]',
                  fadeColorTailwind,
                )}
              />
              <div
                className={cn(
                  'absolute -bottom-0 left-0 z-10 w-full bg-gradient-to-t to-transparent pointer-events-none h-[15rem]',
                  fadeColorTailwind,
                )}
              />
            </div>
          </motion.section>
        )}

        {reviewsCTA && <CTAButton className="mt-[10rem]" data={reviewsCTA} />}
      </div>
    </section>
  );
}
